import { io, Socket } from 'socket.io-client';
import { showToast } from '@/components/Toast';
class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  // 🔥 业务监听器注册表：event -> 回调集合
  // 用于在 reconnect() 替换 socket 实例后，把所有业务监听器重新绑定到新实例上，
  // 避免退出登录后重新登录时，旧实例上的 handler 失效导致收到消息却不渲染。
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();
  // private reconnectAttempts = 0;
  // private maxReconnectAttempts = 5;
  // private reconnectDelay = 1000;
  constructor() {
    // 🔥 不在构造函数中自动连接
    // 等待 token 可用后通过 reconnect() 显式连接
    // 避免退出登录后重新登录时 Authorization 参数为空
    const token = localStorage.getItem('user-token');
    if (token) {
      this.connect();
    }
  }

  /**
   * 🔥 重新连接 socket（用新 token）
   * 在登录成功后调用，确保使用最新的 Authorization token
   */
  public reconnect(): void {
    // 先断开旧连接
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
    // 用新 token 重新连接
    const token = localStorage.getItem('user-token');
    if (token) {
      console.log('🔔 Socket reconnecting with new token...');
      this.connect();
    } else {
      console.warn('⚠️ Socket reconnect skipped: no token available');
    }
  }
  private connect() {
    try {
      const token = localStorage.getItem('user-token') || '';
      // 在开发环境使用相对路径，生产环境需要完整 URL
      this.socket = io('/', {
        path: '/socket.io/',
        transports: ['websocket', 'polling'], // 优先使用 polling，再尝试 websocket
        auth: {
          token: token,
        },
        query: {
          Authorization: token,
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        timeout: 20000,
        forceNew: true, // 强制创建新连接
      });
      this.socket.on('connect', () => {
        this.isConnected = true;
        // this.reconnectAttempts = 0;
      });
      this.socket.on('disconnect', () => {
        this.isConnected = false;
      });
      this.socket.on('connect_error', (error: any) => {
        console.error('Socket.IO connection error:', error);
        console.error('Error details:', {
          message: error.message,
          description: error.description,
          context: error.context,
          type: error.type,
        });
        this.isConnected = false;
        // 🔥 显示连接错误提示
        if (error.message !== 'Authentication failed') {
          showToast(error.message || 'WebSocket connection failed', 'error');
        }
      });
      this.socket.on('reconnect', () => {
        this.isConnected = true;
        // this.reconnectAttempts = 0;
      });
      this.socket.on('reconnect_error', error => {
        console.error('Socket.IO reconnection error:', error);
      });
      // 🔥 关键修复：把注册表中的所有业务监听器重新绑定到新创建的 socket 实例
      // 否则 reconnect() 换新实例后，之前通过 on() 注册的 handler（如 skill 的
      // chat_message 监听）仍绑在被丢弃的旧实例上，导致收到消息却不触发渲染。
      this.rebindListeners();
    } catch (error) {
      console.error('Failed to initialize socket connection:', error);
    }
  }
  public getSocket(): Socket | null {
    return this.socket;
  }
  public getSocketId(): string | undefined {
    return this.socket?.id;
  }
  public isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }
  // 🔥 把注册表中所有业务监听器重新绑定到当前 socket 实例（重连后调用）
  private rebindListeners(): void {
    if (!this.socket) return;
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach(cb => {
        this.socket!.on(event, cb);
      });
    });
  }
  public on(event: string, callback: (...args: any[]) => void): void {
    // 🔥 存入注册表，确保 reconnect 换实例后能自动重新绑定
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(callback);
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }
  public off(event: string, callback?: (...args: any[]) => void): void {
    // 🔥 同步从注册表移除，避免重连后重绑已卸载的 handler
    if (callback) {
      const set = this.listeners.get(event);
      if (set) {
        set.delete(callback);
        if (set.size === 0) this.listeners.delete(event);
      }
    } else {
      this.listeners.delete(event);
    }
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
  public emit(event: string, ...args: any[]): void {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, ...args);
    } else {
      console.warn('Socket not connected, cannot emit event:', event);
    }
  }
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }
}
// Create a singleton instance
const socketService = new SocketService();
export default socketService;
